import { createContext, PropsWithChildren, useContext, useEffect } from 'react';
import { User } from '../types/auth.model';
import { useAuthStore } from '../store';
import { useGetMe } from '../hooks';
import Loading from '../pages/loading';

const AuthContext = createContext<User | null>(null);

type AuthProviderProps = PropsWithChildren;

export default function AuthProvider({ children }: AuthProviderProps) {
  const auth = useAuthStore();
  const { data: me, isError, isLoading } = useGetMe();
  useEffect(() => {
    if (isError) {
      auth.logout();
    }
  }, [isLoading, isError]);

  // The user was cached at login and never refreshed, so a permission change
  // only took effect after logging out and back in -- even though /users/me was
  // already being fetched on every load and its answer thrown away. Writing it
  // back means a granted module appears on a refresh.
  useEffect(() => {
    if (me) {
      auth.setUser(me);
    }
  }, [me]);

  if (isLoading) return <Loading />;
  return (
    <AuthContext.Provider value={auth.user}>{children}</AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
